import { IEditorNode, EditorNodeType } from '../Services/ServiceEditor'
import { GUID } from '../Services/ServiceTypes'
import Util from '../Util'
import { FolderNode } from './folders'

export type ViewStyle = 'byFolder' | 'sortedDesc'

export const mapEditorNodeToFolderNodeBase = (en: IEditorNode) => {
  return {
    id: en.Id,
    name: en.Name,
    children: undefined,
    parentId: en.ParentNodeId,
    isOpen: false,
    isLocked: en.Locked,
    lastEditTextTimestamp: en.LastEditTextTimestamp as any, // hvis du har Date|string i input
    tags: en.Tags,
    numberOfTexts: 0,
    path: '', // is used for "sorted by change date".. Is set afterwards.. after mapEditorNodesToFolderNodes
  } as FolderNode
}

const rekMapEditorNodesToFolderNodes = (
  editorNodes: IEditorNode[],
  editorNodesLookupByParentId: Map<string, IEditorNode[]>,
  folderOpenIds: Set<GUID>,
): FolderNode[] => {
  return editorNodes.map(en => {
    // Hvis det ikke er en folder i editorNodes, kan du stadig lave en node,
    // men children bliver undefined og numberOfTexts er 1 hvis Text ellers 0.
    if (en.Type !== EditorNodeType.Folder) {
      return {
        ...mapEditorNodeToFolderNodeBase(en),
        children: undefined,
        isOpen: folderOpenIds.has(en.Id),
        numberOfTexts: en.Type === EditorNodeType.Text ? 1 : 0,
      }
    }

    const childEditorNodes = editorNodesLookupByParentId.get(en.Id) ?? []
    const childFolderNodes = rekMapEditorNodesToFolderNodes(childEditorNodes, editorNodesLookupByParentId, folderOpenIds)

    // numberOfTexts for folder = summen af alle børns numberOfTexts
    const numberOfTexts = childFolderNodes.reduce((sum, c) => sum + (c.numberOfTexts ?? 0), 0)

    return {
      ...mapEditorNodeToFolderNodeBase(en),
      children: childFolderNodes,
      isOpen: folderOpenIds.has(en.Id),
      numberOfTexts,
    }
  })
}

const buildPathForNode = (node: FolderNode, nodes: FolderNode[] = []): string => {
  // Node i root => ingen path
  if (!node.parentId) return ''

  // Finder stien (som noder) fra root -> target (inkl. target)
  const findPath = (currentNodes: FolderNode[], targetId: string, acc: FolderNode[] = []): FolderNode[] | null => {
    for (const n of currentNodes) {
      const nextAcc = [...acc, n]
      if (n.id === targetId) {
        return nextAcc
      }
      if (n.children?.length) {
        const res = findPath(n.children, targetId, nextAcc)
        if (res) return res
      }
    }
    return null
  }

  const fullPath = findPath(nodes, node.id)
  if (!fullPath) return '' // ikke fundet i træet

  // fullPath: [root, ..., parent, node]
  // Vi vil: uden root (første) og uden node (sidste) => kun "mellemled"
  const middle = fullPath.slice(1, -1)

  // Hvis node ligger direkte under root => middle er tom => ""
  return middle.map(n => n.name).join(' / ')
}

export const mapEditorNodesToFolderNodes = (editorNodes: IEditorNode[], folderOpenIds: Set<GUID>, viewStyle: ViewStyle): FolderNode[] => {
  const editorNodesLookupByParentId = Util.groupBy(
    editorNodes.filter(x => x.ParentNodeId),
    x => x.ParentNodeId!,
  )
  const rootNodes = editorNodes.filter(x => !x.ParentNodeId)
  const folderStructure = rekMapEditorNodesToFolderNodes(rootNodes, editorNodesLookupByParentId, folderOpenIds)

  if (viewStyle === 'byFolder') {
    return folderStructure
  }

  const items = editorNodes.filter(e => e.Type !== EditorNodeType.Folder)
  return items
    .sort((a, b) => ((a.LastEditTextTimestamp as Date) < (b.LastEditTextTimestamp as Date) ? 1 : -1))
    .map(mapEditorNodeToFolderNodeBase)
    .map(folderNode => {
      return {
        ...folderNode,
        path: buildPathForNode(folderNode, folderStructure),
      }
    })
}
